import { effect, Injectable, linkedSignal, signal, WritableSignal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GridScrollStateService {
  private scrollState = new Map<string, number | null>();
  private gridKey: WritableSignal<string> = signal(null);
  private readonly previousGridKey: WritableSignal<string> = linkedSignal({
    source: this.gridKey,
    computation: (_, previous) => previous?.source
  });

  constructor() {
    effect(() => {
      const gridKey = this.gridKey();
      const previousGridKey = this.previousGridKey();
      if (gridKey !== previousGridKey) {
        this.clearAll();
      }
    });
  }

  /**
   * Stores the last clicked row index for a specific grid.
   * @param gridKey A unique identifier for the grid instance.
   * @param index The row index to store.
   */
  setScrollIndex(gridKey: string, index: number): void {
    this.setGridKey(gridKey);
    this.scrollState.set(gridKey, index);
  }

  setGridKey(gridKey: string): void {
    this.gridKey.set(gridKey);
  }

  /**
   * Retrieves the stored row index for a specific grid.
   * Returns null if no index is stored for the key.
   * @param gridKey A unique identifier for the grid instance.
   */
  getScrollIndex(gridKey: string): number | null {
    this.setGridKey(gridKey);
    const index = this.scrollState.get(gridKey);
    return index ?? null; // Return null if undefined
  }

  /**
   * Clears the stored row index for a specific grid.
   * (Optional: Use if you need to explicitly reset the state)
   * @param gridKey A unique identifier for the grid instance.
   */
  clearScrollIndex(gridKey: string): void {
    this.scrollState.delete(gridKey);
  }

  /**
   * Clears all stored scroll states.
   * (Optional: Use on logout or major state reset)
   */
  clearAll(): void {
    this.scrollState.clear();
  }
}
